import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, EyeOff, Eye, CheckCheck, ArrowLeft, Shield } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "em_admin_token";

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("sos"); // "sos" | "encouragements" | "contact"
  const [sosList, setSosList] = useState([]);
  const [encList, setEncList] = useState([]);
  const [contactList, setContactList] = useState([]);

  const authHeaders = (t = token) => ({ headers: { Authorization: `Bearer ${t}` } });

  const login = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    try {
      setLoading(true);
      const r = await api.post("/admin/login", { password });
      const t = r.data.token;
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      setPassword("");
      toast.success("Bienvenue dans le panneau de modération.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setSosList([]);
    setEncList([]);
  };

  const loadAll = async (t) => {
    try {
      const [sos, enc, contact] = await Promise.all([
        api.get("/sos/messages", authHeaders(t)),
        api.get("/admin/encouragements", authHeaders(t)),
        api.get("/admin/contact", authHeaders(t)),
      ]);
      setSosList(sos.data || []);
      setEncList(enc.data || []);
      setContactList(contact.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée. Reconnecte-toi.");
        logout();
      } else {
        toast.error("Chargement impossible.");
      }
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const markRead = async (id) => {
    try {
      await api.post(`/admin/sos/${id}/read`, {}, authHeaders());
      setSosList((cur) => cur.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
      toast.success("Message marqué comme lu.");
    } catch {
      toast.error("Action impossible.");
    }
  };

  const toggleHide = async (id, hidden) => {
    try {
      await api.post(`/admin/encouragements/${id}/hide`, { hidden }, authHeaders());
      setEncList((cur) => cur.map((e) => (e.id === id ? { ...e, is_hidden: hidden } : e)));
      toast.success(hidden ? "Message masqué du mur." : "Message réaffiché.");
    } catch {
      toast.error("Action impossible.");
    }
  };

  const markContactRead = async (id) => {
    try {
      await api.post(`/admin/contact/${id}/read`, {}, authHeaders());
      setContactList((cur) => cur.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
      toast.success("Message marqué comme lu.");
    } catch {
      toast.error("Action impossible.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B1120] text-slate-100 flex items-center justify-center px-5">
        <div className="w-full max-w-md em-card p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange-500/15 border border-orange-500/30 grid place-items-center">
              <Shield className="w-5 h-5 text-orange-300" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-white font-[Outfit]">Panneau de modération</h1>
              <p className="text-sm text-slate-400">Accès réservé à l'équipe.</p>
            </div>
          </div>
          <form onSubmit={login} className="mt-6 grid gap-3" data-testid="admin-login-form">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe admin"
              data-testid="admin-password-input"
              className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:border-orange-500/60"
              autoFocus
              required
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-full"
            >
              <Lock className="w-4 h-4" />
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="admin-back-home"
            className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const unreadCount = sosList.filter((m) => !m.is_read).length;

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-[#0B1120]/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-orange-400" />
            <h1 className="font-[Outfit] font-semibold text-lg">Modération — L'Équipe & Mental</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm text-slate-300 hover:bg-white/5"
            >
              <ArrowLeft className="w-4 h-4" /> Site
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 px-4 py-2 rounded-full text-sm"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center gap-2 mb-6 flex-wrap" role="tablist">
          <button
            role="tab"
            onClick={() => setTab("sos")}
            data-testid="admin-tab-sos"
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              tab === "sos" ? "bg-orange-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"
            }`}
          >
            Messages SOS {unreadCount > 0 && <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-xs">{unreadCount}</span>}
          </button>
          <button
            role="tab"
            onClick={() => setTab("contact")}
            data-testid="admin-tab-contact"
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              tab === "contact" ? "bg-orange-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"
            }`}
          >
            Contact privé {contactList.filter((m) => !m.is_read).length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-xs">
                {contactList.filter((m) => !m.is_read).length}
              </span>
            )}
          </button>
          <button
            role="tab"
            onClick={() => setTab("encouragements")}
            data-testid="admin-tab-enc"
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              tab === "encouragements" ? "bg-orange-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"
            }`}
          >
            Mur d'encouragements
          </button>
        </div>

        {tab === "sos" && (
          <div className="grid gap-3" data-testid="admin-sos-list">
            {sosList.length === 0 ? (
              <p className="text-slate-500 italic">Aucun message pour l'instant.</p>
            ) : (
              sosList.map((m) => (
                <article
                  key={m.id}
                  className={`em-card p-5 ${m.is_read ? "opacity-70" : ""}`}
                  data-testid={`admin-sos-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-semibold">{m.nickname || "Anonyme"}</span>
                      {!m.is_read && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-200">
                          Non lu
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(m.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                  <p className="mt-3 text-slate-100 whitespace-pre-wrap">{m.message}</p>
                  <div className="mt-4 flex items-center justify-between gap-3 flex-wrap text-sm">
                    <p className="text-slate-400">
                      Contact :{" "}
                      <span className="text-slate-200">
                        {m.contact ? m.contact : "non fourni"}
                      </span>
                    </p>
                    {!m.is_read && (
                      <button
                        onClick={() => markRead(m.id)}
                        className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-4 py-2 rounded-full"
                      >
                        <CheckCheck className="w-4 h-4" /> Marquer comme lu
                      </button>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        {tab === "encouragements" && (
          <div className="grid gap-3" data-testid="admin-enc-list">
            {encList.length === 0 ? (
              <p className="text-slate-500 italic">Aucun message encore.</p>
            ) : (
              encList.map((e) => (
                <article key={e.id} className={`em-card p-5 ${e.is_hidden ? "opacity-60" : ""}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <span className="text-white font-semibold">{e.author}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(e.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                  <p className="mt-3 text-slate-100">« {e.message} »</p>
                  <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-slate-400">
                      {e.is_hidden ? "Masqué du mur public" : "Visible publiquement"}
                    </span>
                    <button
                      onClick={() => toggleHide(e.id, !e.is_hidden)}
                      data-testid={`admin-toggle-${e.id}`}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold ${
                        e.is_hidden
                          ? "bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                          : "bg-rose-500/90 hover:bg-rose-600 text-white"
                      }`}
                    >
                      {e.is_hidden ? (
                        <>
                          <Eye className="w-4 h-4" /> Réafficher
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4" /> Masquer
                        </>
                      )}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        {tab === "contact" && (
          <div className="grid gap-3" data-testid="admin-contact-list">
            {contactList.length === 0 ? (
              <p className="text-slate-500 italic">Aucun message privé pour l'instant.</p>
            ) : (
              contactList.map((m) => (
                <article
                  key={m.id}
                  className={`em-card p-5 ${m.is_read ? "opacity-70" : ""}`}
                  data-testid={`admin-contact-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-semibold">{m.name || "Anonyme"}</span>
                      {!m.is_read && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-200">
                          Non lu
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(m.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                  <p className="mt-3 text-slate-100 whitespace-pre-wrap">{m.message}</p>
                  <div className="mt-4 flex items-center justify-between gap-3 flex-wrap text-sm">
                    <p className="text-slate-400">
                      Contact :{" "}
                      <span className="text-slate-200">
                        {m.contact ? m.contact : "non fourni"}
                      </span>
                    </p>
                    {!m.is_read && (
                      <button
                        onClick={() => markContactRead(m.id)}
                        className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-4 py-2 rounded-full"
                      >
                        <CheckCheck className="w-4 h-4" /> Marquer comme lu
                      </button>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
